import axios from "axios";
import { sessionService } from "./main";
import ErrorLogger from "../utils/errorLogger";

class Session {
  async start(authToken: string) {
    const resp = await sessionService.add(authToken);
    const { token, user } = resp.data;
    sessionStorage.setItem("authInfo", JSON.stringify({ token, user }));
  }

  async end() {
    sessionStorage.removeItem("authInfo");
  }

  get active() {
    return sessionStorage.getItem("authInfo") !== null;
  }

  get user() {
    const { user } = JSON.parse(sessionStorage.getItem("authInfo") ?? "{}");
    return user;
  }

  get token() {
    const { token } = JSON.parse(sessionStorage.getItem("authInfo") ?? "{}");
    return token;
  }
}

export const session = new Session();

// Function to check if the user is authorized based on their role
export const isAuthorized = (requiredRoles: string[]) => {
  const user = session.user;
  return user && requiredRoles.includes(user.role);
};

/**
 * Create an API with interceptors with/without auth information.
 * Supply options for baseURL and contentType if different from default
 */
export const apiBaseUrl = (() => {
  const baseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_BASE_URL || "http://localhost:5000";
  try {
    return new URL("/api/v1", baseUrl).href;
  } catch (error) {
    console.error("Invalid API URL configuration:", baseUrl);
    return "http://localhost:5000/api/v1";
  }
})();

export function makeApi(
  withAuth = true,
  options = { baseUrl: apiBaseUrl, contentType: "application/json" }
) {
  const api = axios.create({
    baseURL: options.baseUrl,
    headers: { "Content-type": options.contentType },
    withCredentials: true,
  });

  if (withAuth) {
    // Add Authorization header to requests
    api.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem("token");
        // FIX ME - LATER IMPLEMENT THE SESSION MENAGEMENT ON THE BACKEND
        if (!token) {
          const error = new Error(
            "Your login session is no longer active; please relogin and try again"
          );
          ErrorLogger.logError(error, {
            endpoint: config.url,
            method: config.method?.toUpperCase(),
            context: 'Request Interceptor - Missing Token',
          });
          throw error;
        }
        config.headers["Authorization"] = `${token}`;

        // Log successful request (optional, can be removed in production)
        if (import.meta.env.DEV) {
          ErrorLogger.logInfo(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            hasAuth: !!token,
          });
        }

        return config;
      },
      (error) => {
        ErrorLogger.logError(error, {
          context: 'Request Interceptor - Request Setup Error',
        });
        return Promise.reject(error);
      }
    );

    // Interceptor for response with session expiration handling
    api.interceptors.response.use(
      (response) => {
        // Log successful response in dev mode (optional)
        if (import.meta.env.DEV) {
          ErrorLogger.logInfo(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
        }
        return response;
      },
      async (error) => {
        // Log all API errors with detailed information
        ErrorLogger.logApiError(error, error.config);

        if (error.response && error.response.status === 403) {
          const errorMessage = error.response.data.message || "Unauthorized";
          if (
            errorMessage === "Invalid token" ||
            errorMessage === "Token expired" ||
            errorMessage === "Unauthorized"
          ) {
            ErrorLogger.logWarning("Session expired - redirecting to login", {
              errorMessage,
              endpoint: error.config?.url,
            });
            sessionStorage.removeItem("token");
            // await showAlert('Session expired. Please log in again');
            // Redirect after the alert is dismissed
            window.location.href = "/";
          }
        }
        return Promise.reject(error);
      }
    );
  } else {
    // For non-auth requests, still log errors
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        ErrorLogger.logApiError(error, error.config);
        return Promise.reject(error);
      }
    );
  }

  return api;
}

/**
 * api to call backend services with authentication, with json content type
 */
export const apiWithAuth = makeApi(true);

/**
 * api to call backend services without authentication, with json content type
 */
export const apiWithoutAuth = makeApi(false);
